import * as React from "react";
import { Row, Col, Skeleton, Upload } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import MeshViewer from 'components/MeshViewer';

const { Dragger } = Upload;

/**
 * ScanGrid Component
 * 
 * Grid layout component for displaying scan data with optional file upload functionality.
 * Renders scan items in a responsive grid with support for STL file viewing and upload.
 * 
 * Features:
 * - Responsive grid layout
 * - STL file visualization using MeshViewer
 * - File upload functionality
 * - UUID detection for labels
 * - Loading state management
 * - Overlay label display
 * 
 * @param {Object} props - Component props
 * @param {Array} props.scans - Array of scan objects to display
 * @param {Function} [props.onFileSelect] - Callback function for file selection
 * @returns {JSX.Element} The scan grid component
 */
export function ScanGrid({
  scans = [],
  onFileSelect
}) {
  const [loading, setLoading] = React.useState(false);
  const [scanData, setScanData] = React.useState([]);

  React.useEffect(() => {
    setLoading(true);
    const tmpScans = scans.map((scan) => {
      scan.isStl = !!scan.downloadUrl;
      return scan;
    });

    setScanData(tmpScans);
    setLoading(false);
  }, [scans]);

  // Function to check if a string is a valid UUID
  const isUUID = (label) => {
    const regexExp = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
    return regexExp.test(label);
  };

  function renderItem(item) {
    if(item.isStl) {
        return <MeshViewer url={item.downloadUrl} width={200} height={200} style={{ width: '100%' }}/>
    }

    let detailItem;
    if (onFileSelect) {
        const draggerProps = {
            name: 'file',
            multiple: false,
            accept: '.stl',
            customRequest: ({ file, onSuccess }) => { 
                console.log('customRequest', file);
                onFileSelect(file, item.occurrenceId);
                onSuccess("ok");
            },
        };

        detailItem = (
            <Dragger {...draggerProps}>
                <div className="upload-scan-container">
                    <div className="main-section">
                        <PlusCircleOutlined className="plus-icon" />
                    </div>
                    <div className="remaining-section">
                        {/* Optional content for the remaining section */}
                    </div>
                </div>
            </Dragger>
        );
    } else {
        detailItem = (
            <div className="upload-scan-container">
                <div className="main-section">
                    {/* The PlusCircleOutlined icon is omitted here */}
                </div>
                <div className="remaining-section">
                    {/* Optional content for the remaining section */}
                </div>
            </div>
        );
    }

    return detailItem;
  }

  return (
    <Skeleton loading={loading}>
      <Row gutter={[16, 16]}>
        {scanData.map((scan, index) => (
          <Col key={scan.fileName || scan.downloadUrl || `scan-${index}`} xs={24} sm={12} md={8} lg={6}>
            <div className="image-overlay-container">
              {renderItem(scan)}
              {/* Conditionally render overlay-label based on whether scan.label is a UUID */}
              {!isUUID(scan.label) && (
                <div className="overlay-label"><div className="label-text">{scan.label}</div></div>
              )}
            </div>
          </Col>
        ))}
      </Row>
    </Skeleton>
  );
}
